import React, { Component } from 'react';
import { Link, Redirect } from "react-router-dom";
import { Formik } from 'formik';
import _ from 'lodash';
import config from '../config';
import JsonEditorComponent from '../JsonEditorComponent';
import { AuthContext } from "../context/auth";
import fetcher from "../context/fetcher";
import { downloadFile, readFileAsync } from "../components/ImportExport";

export default class Cohorts extends Component {
    static contextType = AuthContext;

    constructor(props, context) {
        super(props);
        this.authContext = context;
        this.state = {
            cohorts: null,
            visibleColumns: [],
            vars: []
        };
        this.init = this.init.bind(this);
        this.remove = this.remove.bind(this);
        this.exportObjs = this.exportObjs.bind(this);
        this.importObjs = this.importObjs.bind(this);
        this.toggleColumn = this.toggleColumn.bind(this);
        this.loadColumnSettings = this.loadColumnSettings.bind(this);
        this.saveColumnSettings = this.saveColumnSettings.bind(this);
        this.formatConditionForKey = this.formatConditionForKey.bind(this);
        this.formatRange = this.formatRange.bind(this);
    }

    componentDidMount() {
        this.loadColumnSettings();
        this.init();
    }

    async init() {
        try {
            const [varsResponse, cohortsResponse] = await Promise.all([
                fetcher(`${config.API()}collection/user-save-vars`),
                fetcher(`${config.API()}collection/cohorts`)
            ]);
            const vars = await varsResponse.json();
            const cohorts = await cohortsResponse.json();
            this.setState({
                vars: _.sortBy(vars, ['key']),
                cohorts
            }, () => {
                // Устанавливаем visibleColumns по умолчанию, если не загружены
                if (this.state.visibleColumns.length === 0) {
                    const defaultColumns = ['title', ...this.state.vars.map(v => v.key)];
                    this.setState({ visibleColumns: defaultColumns }, this.saveColumnSettings);
                }
            });
        } catch (err) {
            console.error('Error initializing cohorts:', err);
        }
    }

    loadColumnSettings() {
        const savedColumns = localStorage.getItem('cohortTableColumns');
        if (savedColumns) {
            this.setState({ visibleColumns: JSON.parse(savedColumns) });
        }
    }

    saveColumnSettings() {
        localStorage.setItem('cohortTableColumns', JSON.stringify(this.state.visibleColumns));
    }

    toggleColumn(columnKey) {
        this.setState(prevState => {
            const visibleColumns = prevState.visibleColumns.includes(columnKey)
                ? prevState.visibleColumns.filter(key => key !== columnKey)
                : [...prevState.visibleColumns, columnKey];
            return { visibleColumns };
        }, this.saveColumnSettings);
    }

    remove = (eventId) => {
        return (e) => {
            const isOk = window.confirm("Are you sure?");
            if (!isOk) return;
            fetcher(`${config.API()}collection/cohorts/remove/${eventId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: "{}"
            })
                .then(response => response.json())
                .then(data => {
                    const cohorts = this.state.cohorts.filter(x => x._id !== eventId);
                    this.setState({ cohorts });
                })
                .catch(err => console.error(`Error removing cohort ${eventId}:`, err));
        };
    };

    linkToEdit = (eventId) => {
        return () => {
            this.setState({ redirect: `/cohorts/${eventId}` });
        };
    };

    exportObjs = () => downloadFile(JSON.stringify(this.state.cohorts, null, "\t"), "cohorts.json", 'text/plain');

    importObjs = async (e) => {
        try {
            const text = await readFileAsync(e);
            const objs = JSON.parse(text);
            await fetcher(`${config.API()}collection/cohorts/import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: text
            });
            this.setState({ cohorts: objs });
        } catch (err) {
            console.error('Error importing cohorts:', err);
        }
    };

    formatConditionForKey(cohort, key) {
        if (!cohort.conditions || !Array.isArray(cohort.conditions)) return '';
        const condition = cohort.conditions.find(c => c.key === key);
        if (!condition) return '';
        const { condition: cond, value, min, max } = condition;
        switch (cond) {
            case 'equals':
                return `= ${value || ''}`;
            case 'greater_than':
                return `> ${value || ''}`;
            case 'less_than':
                return `< ${value || ''}`;
            case 'range':
                return `in [${min || ''}, ${max || ''}]`;
            case 'exists':
                return `exists`;
            default:
                return `${cond}`;
        }
    }

    formatRange(min, max) {
        if (min !== null && min !== undefined && max !== null && max !== undefined) {
            return `${min} - ${max}`;
        } else if (min !== null && min !== undefined) {
            return `${min} - ...`;
        } else if (max !== null && max !== undefined) {
            return `... - ${max}`;
        }
        return '';
    }

    render() {
        if (this.state.redirect) {
            return <Redirect push to={this.state.redirect} />;
        }

        const availableColumns = [
            ...this.state.vars.map(varItem => ({
                key: varItem.key,
                label: varItem.key
            })),
            { key: 'lastVisit', label: 'Last Visit' },
            { key: 'daysFromRegistration', label: 'Days From Registration' },
            { key: 'buildVersion', label: 'Build Version' }
        ];

        return (
            <div>
                <div className="card shadow w100">
                    <div className="card-header py-3 d-flex justify-content-between align-items-center">
                        <h2 className="m-0 font-weight-bold">Cohorts</h2>
                        <div>
                            <input
                                onChange={this.importObjs}
                                type="file"
                                id="file-input"
                                className="btn btn-icon btn-light ml-1"
                            />
                            <button
                                onClick={this.exportObjs}
                                className="btn btn-icon btn-light ml-1"
                            >
                                <i className="fas fa-fw fa-arrow-circle-down"></i> Export
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="d-flex flex-wrap">
                                    {availableColumns.map(col => (
                                        <div key={col.key} className="form-check mr-3 small">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`column-${col.key}`}
                                                checked={this.state.visibleColumns.includes(col.key)}
                                                onChange={() => this.toggleColumn(col.key)}
                                            />
                                            <label className="form-check-label" htmlFor={`column-${col.key}`}>
                                                {col.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table
                                className="dataTable table table-align-middle table-bordered table-sm"
                                width="100%"
                                cellSpacing="0"
                            >
                                <thead>
                                <tr>
                                    <th>title</th>
                                    {this.state.vars
                                        .filter(varItem => this.state.visibleColumns.includes(varItem.key))
                                        .map(varItem => (
                                            <th key={varItem.key}>{varItem.key}</th>
                                        ))}
                                    {this.state.visibleColumns.includes('lastVisit') && <th>days was absent</th>}
                                    {this.state.visibleColumns.includes('daysFromRegistration') && <th>days after registration</th>}
                                    {this.state.visibleColumns.includes('buildVersion') && <th>build Version</th>}
                                    <th></th>
                                </tr>
                                </thead>
                                <tbody>
                                {this.state.cohorts && this.state.cohorts.map((x) => (
                                    <tr key={x._id}>
                                        <td>{x.title}</td>
                                        {this.state.vars
                                            .filter(varItem => this.state.visibleColumns.includes(varItem.key))
                                            .map(varItem => (
                                                <td key={varItem.key}>{this.formatConditionForKey(x, varItem.key)}</td>
                                            ))}
                                        {this.state.visibleColumns.includes('lastVisit') && (
                                            <td>{this.formatRange(x.lastVisitMin, x.lastVisitMax)}</td>
                                        )}
                                        {this.state.visibleColumns.includes('daysFromRegistration') && (
                                            <td>{this.formatRange(x.daysFromRegistrationMin, x.daysFromRegistrationMax)}</td>
                                        )}
                                        {this.state.visibleColumns.includes('buildVersion') && (
                                            <td>{this.formatRange(x.buildVersionMin, x.buildVersionMax)}</td>
                                        )}
                                        <td>
                                            <button
                                                className="btn btn-outline-danger btn-circle btn-sm mr-1"
                                                onClick={this.remove(x._id)}
                                            >
                                                <i className="fa fa-trash"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-primary btn-circle btn-sm"
                                                onClick={this.linkToEdit(x._id)}
                                            >
                                                <i className="fa fa-edit"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="r mt-3">
                            <button
                                onClick={this.linkToEdit("new")}
                                className="btn btn-icon btn-primary ml-1"
                            >
                                <span className="fas fa-fw fa-plus-square"></span> Add
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}